import React, { useMemo, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { 
  ChevronLeftIcon, 
  EllipsisHorizontalIcon
} from '@heroicons/react/24/outline';
import { Plus, History, ArrowRight, Users, UserPlus, X, Globe, Lock, Trash2, Link as LinkIcon, LogOut, Play, Calendar, Settings, ChevronRight, Archive, Flag } from 'lucide-react';
import ObjectivesList from './ObjectivesList';
import { motion, AnimatePresence } from 'framer-motion';
import { useProjectStore } from '../stores/projectStore';
import ReviewModal from './reviews/ReviewModal';
import VenueBacklink from './VenueBacklink';
import TourRecapHero from './TourRecapHero';
import EditProjectModal from './EditProjectModal';
import ProjectShareModal from './ProjectShareModal';
import ProjectReportModal from './report/ProjectReportModal';
import DiscoveriesSection from './DiscoveriesSection';
import WorldModelDebugPanel from './WorldModelDebugPanel';
import useMessagePins from '../hooks/useMessagePins';
import { trackAPI, projectAPI, reviewAPI, createQueryKey, chatAPI, authAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { getImageUrl } from '../config/runtime';
import { getProjectTrailPoints, normalizeProjectGeolocation } from '../utils/projectLocation';
import {
  buildCanonicalProjectShareUrl,
  buildProjectShareClipboardText,
  copyTextToClipboard,
  shareProjectNatively,
} from '../utils/projectShare';
import toast from 'react-hot-toast';

// Constants
const MAP_BOUNDS_PADDING_PERCENT = 0.15;
const MAP_BOUNDS_MIN_PADDING = 0.005;

const toFiniteNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

/**
 * Converts inventory items into map-friendly discovery pins.
 * Each item with valid geolocation becomes a pin; nearby items are grouped.
 * 
 * Design rationale (Tufte principle): Show the data, not decoration.
 * Each pin = one discovery. Clusters = multiple discoveries at same spot.
 */
const aggregateInventoryToDiscoveryPins = (inventory = []) => {
  if (!Array.isArray(inventory) || inventory.length === 0) return [];

  const CLUSTER_PRECISION = 4; // ~11m precision for clustering nearby discoveries
  const groups = new Map();
  
  const getTimestamp = (item) => {
    const ts = item?.found_at || item?.foundAt || item?.timestamp || item?.createdAt || item?.updatedAt;
    const date = ts ? new Date(ts) : null;
    return date && !isNaN(date.getTime()) ? date.getTime() : 0;
  };

  // Group items by approximate location
  inventory.forEach((item) => {
    const lat = toFiniteNumber(item?.found_at_lat ?? item?.location?.lat ?? item?.lat);
    const lng = toFiniteNumber(item?.found_at_lng ?? item?.location?.lng ?? item?.lng);
    if (lat === null || lng === null) return;
    
    const key = `${lat.toFixed(CLUSTER_PRECISION)}:${lng.toFixed(CLUSTER_PRECISION)}`;
    const existing = groups.get(key) || { lat, lng, items: [] };
    existing.items.push(item);
    groups.set(key, existing);
  });

  return Array.from(groups.values()).map((group, idx) => {
    // Sort by timestamp (newest first) to show most recent as primary
    const sorted = group.items.slice().sort((a, b) => getTimestamp(b) - getTimestamp(a));
    const primary = sorted[0] || group.items[0];
    
    // Collect all images for gallery view
    const allImages = sorted
      .map(item => item?.image_url || item?.imageUrl)
      .filter(Boolean)
      .slice(0, 4); // Max 4 for preview grid
    
    // Collect all discoverers
    const discoverers = [...new Set(sorted
      .map(item => item?.found_by_username)
      .filter(Boolean)
    )];

    return {
      id: `discovery-${idx}`,
      type: 'discovery', // Semantic type for rendering
      lat: group.lat,
      lng: group.lng,
      count: group.items.length,
      // Primary item info (shown on pin)
      name: primary?.name || primary?.common_name || primary?.taxonomy?.common_name || 'Discovery',
      imageUrl: primary?.image_url || primary?.imageUrl,
      // All items in this cluster
      items: sorted,
      allImages,
      // Context
      locationNote: primary?.location_note,
      timestamp: primary?.found_at || primary?.foundAt || primary?.timestamp,
      discoverers,
      // For visual differentiation
      hasPhoto: Boolean(primary?.image_url || primary?.imageUrl),
    };
  });
};

// Utility function to format time ago
const formatTimeAgo = (date) => {
  const now = new Date();
  const diffMs = now - date;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  
  // For older dates, show formatted date
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
};

const PROJECT_TAB_IDS = new Set(['summary', 'chat', 'members', 'journal']);

const getRequestedProjectTab = (tab) => {
  return PROJECT_TAB_IDS.has(tab) ? tab : null;
};

const ProjectView = ({ project, chatId, projectId, openReviewOnMount = false, showBackLink = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { clearProject } = useProjectStore();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [replayPath, setReplayPath] = useState([]);
  
  // UI: Menu and delete confirm
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const menuButtonRef = useRef(null);

  // UI state mirroring ExploreDetailPage patterns (simplified)
  const [activeTab, setActiveTab] = useState(() => getRequestedProjectTab(location.state?.tab) || 'summary'); // 'summary' | 'chat' | 'members' | 'journal'
  
  // Scroll detection for mobile header background
  const [isScrolledPastImage, setIsScrolledPastImage] = useState(false);
  const mobileHeroRef = useRef(null);
  
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [reviewingSessionId, setReviewingSessionId] = useState(null); // For session-level reviews
  const [reviewingSessionTitle, setReviewingSessionTitle] = useState(null);
  const lastStatusRef = useRef(project?.status);
  
  // Member management state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Legacy "event/game-mode/scheduling" state removed in the simplified model.
  // Projects behave as endless "site" projects with session history.
  const isSiteProject = true;
  
  // Determine if project is finished
  const isFinished = useMemo(() => {
    return project?.status === 'finished';
  }, [project?.status]);

  // Tour completed = every objective checked off. Site projects never flip
  // project.status to 'finished' (sessions archive instead), so objective
  // state is the only reliable end-of-tour signal on the overview.
  const tourCompleted = useMemo(() => {
    const objectives = project?.objectives || [];
    return objectives.length > 0 && objectives.every((o) => o.isCompleted === true);
  }, [project?.objectives]);
  
  // Check if user is owner (defined early for use in other useMemos)
  const isOwner = useMemo(() => {
    return Boolean(user && project && user.id === project.ownerId);
  }, [user, project]);
  
  // For public projects, only owner can fetch participants/chats initially
  // (participants who aren't owner access via /projects route, not /discover)
  const mayHaveFullAccess = useMemo(() => {
    // Wait for project to load before determining access
    if (!project) return false;
    // If not a public project, anyone with projectId can try to access
    if (!project.isPublic) return true;
    // For public projects, only owner definitely has access
    return Boolean(isOwner);
  }, [project, isOwner]);
  
  // Legacy event-only statuses (pending/waiting) removed from UI.
  
  
  // Fetch all team members — gated only on login (not mayHaveFullAccess) so collaborators
  // on public projects still get the member list for activity-log attribution.
  // The backend returns 403 for non-members, which React Query silently ignores.
  const { data: participantsResp, isLoading: isLoadingParticipants } = useQuery({
    queryKey: ['participants', projectId],
    queryFn: () => projectId ? projectAPI.getParticipants(projectId) : Promise.resolve({ data: { data: { participants: [] } } }),
    enabled: !!projectId && !!user,
    staleTime: 60 * 1000,
    retry: false,
  });

  // Fetch project events (the Journal/Quest Log)
  const { data: eventsResp, isLoading: isLoadingEvents } = useQuery({
    queryKey: ['project-events', projectId],
    queryFn: () => projectId ? projectAPI.getEvents(projectId, { limit: 1000 }) : Promise.resolve({ data: [] }),
    enabled: !!projectId,
    staleTime: 30 * 1000, // Refresh every 30 seconds
    refetchInterval: 30 * 1000 // Auto-refetch for real-time updates
  });

  const events = useMemo(() => {
    const eventsList = eventsResp?.data || [];
    // Sort reverse chronological by createdAt (newest first)
    return [...eventsList].sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return dateB - dateA;
    });
  }, [eventsResp]);
  // Combine owner and participants for member list (site projects)
  const allMembers = useMemo(() => {
    const participants = participantsResp?.data?.data?.participants || [];
    const members = [project?.owner, ...participants].filter(Boolean);
    const membersMap = new Map();
    members.forEach(member => {
      if (member?.id && !membersMap.has(member.id)) {
        membersMap.set(member.id, member);
      }
    });
    return Array.from(membersMap.values());
  }, [project?.owner, participantsResp]);
  
  // Fast lookup: userId → username (for activity log attribution)
  const memberById = useMemo(() => {
    const map = new Map();
    allMembers.forEach(m => { if (m?.id) map.set(String(m.id), m.username || m.firstName || m.email); });
    return map;
  }, [allMembers]);

  // Extract participants for use elsewhere
  const participants = useMemo(() => participantsResp?.data?.data?.participants || [], [participantsResp]);

  // Check if current user is a collaborator (not owner)
  const isCollaborator = useMemo(() => {
    if (!user || !project || isOwner) return false;
    // Check if user is in the participants list
    return participants.some(p => p.id === user.id) || allMembers.some(m => m.id === user.id);
  }, [user, project, isOwner, participants, allMembers]);

  // Fetch chat history for all projects (session history)
  // Only fetch for users with full access (owner, or non-public projects)
  const { data: chatsResponse, isLoading: isLoadingChats, refetch: refetchChats } = useQuery({
    queryKey: ['project-chats', projectId],
    queryFn: () => projectId ? chatAPI.getProjectChats(projectId) : Promise.resolve({ data: { data: { chats: [] } } }),
    enabled: Boolean(projectId && mayHaveFullAccess),
    staleTime: 0, // Always refetch when component mounts to ensure fresh data after archiving
    refetchOnMount: true, // Refetch when component mounts to catch changes from other pages
    refetchOnWindowFocus: true // Refetch when window regains focus
  });
  
  // Refetch chats when navigating back from chat page (detect via location state or URL change)
  useEffect(() => {
    if (!projectId || !mayHaveFullAccess) return undefined;

    // Refetch chats when component becomes visible/focused.
    // Public read-only viewers should never hit the chats endpoint.
    const handleFocus = () => {
      refetchChats();
    };

    refetchChats();
    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [projectId, mayHaveFullAccess, refetchChats]);
  
  const chats = useMemo(() => chatsResponse?.data?.data?.chats || [], [chatsResponse]);
  
  
  // Find active chat
  // CRITICAL: isActive and archivedAt are the SOURCE OF TRUTH - message count does NOT matter
  // IMPORTANT: Must filter by current user's ID since backend returns ALL chats for site projects
  const activeChat = useMemo(() => {
    // Early return if user not available
    if (!user?.id) return null;
    
    // Normalize user ID to string for safe comparison (handles edge cases)
    const currentUserId = String(user.id);
    
    const found = chats.find(c => {
      // CRITICAL: Must belong to current user (for site projects, backend returns ALL members' chats)
      // Normalize both IDs to strings for safe comparison (handles edge cases where one might be number/string)
      if (!c.userId) return false; // Safety check: skip chats without userId
      const chatUserId = String(c.userId);
      if (chatUserId !== currentUserId) return false;
      
      // Must be marked as active
      // Check both boolean true and string "true" for robustness
      const isActiveValue = c.isActive === true || c.isActive === 'true';
      if (!isActiveValue) return false;
      
      // CRITICAL: Must NOT be archived (archivedAt should be null/undefined)
      // A chat can have isActive=true but also archivedAt set, which means it's archived
      if (c.archivedAt) {
        return false;
      }
      
      // If isActive === true AND archivedAt === null, chat is active (regardless of message count)
      return true;
    });
    
    return found || null;
  }, [chats, user?.id]);

  // An explicitly ENDED run with partial progress earns the recap too (field
  // finding, Aug 3: a real visitor ended a great walk at 10/15 and got the
  // plain overview — hardly anyone checks off EVERY step, so the all-complete
  // gate made the souvenir invisible in practice). Scoped to starter clones
  // (originalProjectId set): regular endless projects keep the strict gate.
  // Requires the chats query to have SETTLED so a still-loading active chat
  // doesn't flash the recap.
  const tourEnded = useMemo(() => {
    if (!project?.originalProjectId) return false;
    const objectives = project?.objectives || [];
    const completed = objectives.filter((o) => o.isCompleted === true).length;
    if (completed === 0) return false;
    if (isLoadingChats || !chatsResponse) return false;
    return !activeChat;
  }, [project?.originalProjectId, project?.objectives, isLoadingChats, chatsResponse, activeChat]);

  // History chats sorted by most recent (show ALL chats, including active ones)
  const historyChats = useMemo(() => {
    return [...chats]
      .sort((a, b) => {
        const dateA = new Date(a.createdAt || a.updatedAt || 0);
        const dateB = new Date(b.createdAt || b.updatedAt || 0);
        return dateB - dateA;
      });
  }, [chats]);
  
  // Fetch session-level reviews for all chats in this project
  const { data: sessionReviewsResp } = useQuery({
    queryKey: ['session-reviews', projectId],
    queryFn: () => projectId ? reviewAPI.getSessionReviews(projectId) : Promise.resolve({ data: { data: { reviewsByChatId: {} } } }),
    enabled: !!projectId && !!user?.id,
    staleTime: 60 * 1000
  });
  
  // Map of chatId -> review for efficient lookup
  const sessionReviewsByChatId = useMemo(() => {
    return sessionReviewsResp?.data?.data?.reviewsByChatId || {};
  }, [sessionReviewsResp]);
  
  // User search query (for member invitation)
  const { data: userSearchResults, isLoading: isSearchingUsers, error: userSearchError } = useQuery({
    queryKey: ['user-search', userSearchQuery],
    queryFn: () => authAPI.searchUsers(userSearchQuery, 10),
    enabled: userSearchQuery.length >= 2,
    staleTime: 30000
  });
  
  const searchUsers = useMemo(() => userSearchResults?.data?.data?.users || [], [userSearchResults]);

  // Determine if current viewer is the owner or a participant
  const isViewerParticipantOrOwner = useMemo(() => {
    try {
      const viewerId = user?.id || user?._id;
      if (!viewerId) return false;
      const ownerId = project?.ownerId || project?.owner?.id || project?.owner?._id;
      if (ownerId && viewerId === ownerId) return true;
      const list = Array.isArray(participants) ? participants : [];
      return list.some((p) => {
        const pid = p?.id || p?._id || p?.userId;
        return pid && pid === viewerId;
      });
    } catch (_) {
      return false;
    }
  }, [user?.id, user?._id, project?.ownerId, project?.owner, participants]);

  // Check if user can start/resume sessions (for site projects) or start events (for event projects)
  // SECURITY: Only members (owner or collaborators) can start sessions
  // Published projects are read-only for non-members
  const canStartOrResumeSession = useMemo(() => {
    if (!user?.id) return false;
    // Owner can always start/resume
    if (isOwner) return true;
    // Only participants (collaborators) can start/resume - NOT just public viewers
    if (isViewerParticipantOrOwner) return true;
    // Public projects do NOT allow starting sessions without joining
    return false;
  }, [user?.id, isOwner, isViewerParticipantOrOwner]);
  
  // No event-start logic in the simplified model.

  // Check if user should see "Join Project" button (for public projects where user is not a member)
  const isPublished = useMemo(() => project?.isPublic === true, [project?.isPublic]);
  const hasFullProjectAccess = useMemo(() => isOwner || isViewerParticipantOrOwner, [isOwner, isViewerParticipantOrOwner]);
  const isReadOnlyViewer = useMemo(() => isPublished && !hasFullProjectAccess, [isPublished, hasFullProjectAccess]);
  const canReportProject = useMemo(() => Boolean(isPublished && user?.id && !isOwner), [isPublished, user?.id, isOwner]);
  const shareUrl = useMemo(
    () => (projectId ? buildCanonicalProjectShareUrl(projectId) : ''),
    [projectId]
  );

  const userContextQuery = useQuery({
    queryKey: ['project-user-context', projectId, user?.id || user?._id],
    queryFn: async () => {
      const res = await projectAPI.getUserContext(projectId);
      return res?.data?.data ?? res?.data ?? {};
    },
    enabled: Boolean(hasFullProjectAccess && projectId && (user?.id || user?._id)),
    staleTime: 60_000,
  });

  useEffect(() => {
    if (isReadOnlyViewer && activeTab !== 'journal') {
      setActiveTab('journal');
    }
  }, [isReadOnlyViewer, activeTab]);

  useEffect(() => {
    const requestedTab = getRequestedProjectTab(location.state?.tab);
    if (!requestedTab) return;
    if (isReadOnlyViewer && requestedTab !== 'journal') return;
    setActiveTab((currentTab) => (currentTab === requestedTab ? currentTab : requestedTab));
  }, [location.key, location.state?.tab, isReadOnlyViewer]);

  const tabItems = useMemo(() => {
    const teamLabel = allMembers.length > 1 ? `Team (${allMembers.length})` : 'Team';
    const items = [
      { id: 'summary', label: 'Overview' },
      { id: 'chat', label: 'Sessions', icon: History },
      { id: 'members', label: teamLabel, icon: Users },
      { id: 'journal', label: 'Journal', icon: Globe },
    ];
    return isReadOnlyViewer ? items.filter(tab => tab.id === 'journal') : items;
  }, [isReadOnlyViewer, allMembers.length]);

  // Discovery pins from world model inventory - the actual valuable data
  const discoveryPins = useMemo(() => {
    try {
      const inventory = project?.world_state?.inventory;
      return aggregateInventoryToDiscoveryPins(inventory);
    } catch (error) {
      console.warn('Failed to build discovery pins:', error);
      return [];
    }
  }, [project?.world_state?.inventory]);

  // Calculate bounds for the trail to ensure full trail is visible
  const mapBounds = useMemo(() => {
    const points = [];
    const addPoint = (lat, lng) => {
      const latNum = toFiniteNumber(lat);
      const lngNum = toFiniteNumber(lng);
      if (latNum === null || lngNum === null) return;
      points.push([latNum, lngNum]);
    };

    if (Array.isArray(replayPath)) {
      replayPath.forEach(point => {
        if (Array.isArray(point)) {
          addPoint(point[0], point[1]);
        } else if (point && typeof point === 'object') {
          addPoint(point.lat, point.lng);
        }
      });
    }

    discoveryPins.forEach(pin => addPoint(pin.lat, pin.lng));

    const projectTrail = getProjectTrailPoints(project);
    if (projectTrail.length > 0) {
      projectTrail.forEach((point) => addPoint(point?.lat ?? point?.[0], point?.lng ?? point?.[1]));
    } else {
      const centerPoint = normalizeProjectGeolocation(project);
      if (centerPoint) {
        addPoint(centerPoint.lat, centerPoint.lng);
      }
    }

    if (points.length === 0) return null;
    if (points.length === 1) {
      const [lat, lng] = points[0];
      return [
        [lat - MAP_BOUNDS_MIN_PADDING, lng - MAP_BOUNDS_MIN_PADDING],
        [lat + MAP_BOUNDS_MIN_PADDING, lng + MAP_BOUNDS_MIN_PADDING]
      ];
    }

    const lats = points.map(point => point[0]);
    const lngs = points.map(point => point[1]);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    const latPadding = Math.max((maxLat - minLat) * MAP_BOUNDS_PADDING_PERCENT, MAP_BOUNDS_MIN_PADDING);
    const lngPadding = Math.max((maxLng - minLng) * MAP_BOUNDS_PADDING_PERCENT, MAP_BOUNDS_MIN_PADDING);

    return [
      [minLat - latPadding, minLng - lngPadding],
      [maxLat + latPadding, maxLng + lngPadding]
    ];
  }, [replayPath, discoveryPins, project]);

  // Map key for stable rendering

  // Permission: owner or admin can delete
  const canDelete = useMemo(() => {
    try {
      if (!user || !project) {
        return false;
      }
      
      // Normalize IDs to strings for comparison to avoid type mismatches
      const ownerId = String(project.ownerId || project?.owner?.id || project?.owner?._id || '');
      const userId = String(user?.id || user?._id || '');
      
      // Check for valid IDs
      if (!userId || userId === 'undefined' || userId === 'null') {
        return user?.role === 'admin';
      }
      
      if (!ownerId || ownerId === 'undefined' || ownerId === 'null') {
        return false;
      }
      
      // Only owner or admin can delete
      return user.role === 'admin' || ownerId === userId;
    } catch (error) {
      console.error('Error checking delete permissions:', error);
      return false;
    }
  }, [user, project]);

  // Close menu on outside click
  useEffect(() => {
    // Since we use a Portal with a full-screen backdrop, we don't need this manual listener anymore
    // The backdrop div handles the close event
    return () => {};
  }, []);
  
  // Legacy event scheduling / waiting-room logic removed.

  // New session mutation (for site projects)
  const newSessionMutation = useMutation({
    mutationFn: async () => {
      if (!projectId) throw new Error('Missing projectId');
      return chatAPI.startNewSession(projectId);
    },
    onSuccess: async (response) => {
      const newChatId = response?.data?.data?.chatId || response?.data?.chatId;
      if (newChatId) {
        queryClient.invalidateQueries({ queryKey: ['project-chats', projectId] });
        queryClient.invalidateQueries({ queryKey: ['chats', projectId] });
        queryClient.invalidateQueries({ queryKey: createQueryKey.project(projectId) });
        await new Promise(resolve => setTimeout(resolve, 200));
        navigate(`/projects/${projectId}/chats/${newChatId}`);
      } else {
        console.error('[ProjectView] No chatId in response:', response);
        toast.error('Session started but could not navigate to chat');
        queryClient.invalidateQueries(['project-chats', projectId]);
      }
    },
    onError: (error) => {
      toast.error('Failed to start new session');
      console.error('[ProjectView] Error starting session:', error);
    }
  });

  // Add collaborator mutation
  const addCollaboratorMutation = useMutation({
    mutationFn: ({ email, userId }) => {
      const payload = { role: 'VIEWER' };
      if (userId) {
        payload.userId = userId;
      } else if (email) {
        payload.email = email;
      }
      return projectAPI.addCollaborator(projectId, payload);
    },
    onSuccess: () => {
      // toast.success('Member added successfully');
      setUserSearchQuery('');
      setSelectedUser(null);
      setShowInviteModal(false);
      queryClient.invalidateQueries(['participants', projectId]);
      queryClient.invalidateQueries(createQueryKey.project(projectId));
    },
    onError: (error) => {
      let errorMessage = 'Failed to add member';
      if (error?.response?.data) {
        const data = error.response.data;
        if (Array.isArray(data.detail) && data.detail.length > 0) {
          const firstError = data.detail[0];
          errorMessage = firstError?.msg || firstError?.message || firstError?.type || errorMessage;
          if (typeof errorMessage === 'object') {
            errorMessage = JSON.stringify(errorMessage);
          }
        } else if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        } else if (Array.isArray(data) && data.length > 0) {
          errorMessage = data[0]?.msg || data[0]?.message || errorMessage;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      console.error('[ProjectView] Add collaborator error:', error);
    }
  });

  // Remove collaborator mutation
  const removeCollaboratorMutation = useMutation({
    mutationFn: (userId) => projectAPI.removeCollaborator(projectId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries(['participants', projectId]);
      queryClient.invalidateQueries(createQueryKey.project(projectId));
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to remove member';
      toast.error(errorMessage);
      console.error('[ProjectView] Remove collaborator error:', error);
    }
  });


  // Leave project mutation (for non-owners)
  const leaveProjectMutation = useMutation({
    mutationFn: () => projectAPI.leave(projectId),
    onSuccess: () => {
      toast.success('You have left the project');
      
      // Invalidate all queries to ensure fresh data
      queryClient.invalidateQueries(['participants', projectId]);
      queryClient.invalidateQueries(createQueryKey.project(projectId));
      queryClient.invalidateQueries(['project-chats', projectId]); // Invalidate chats (active chat was archived)
      queryClient.invalidateQueries(['chats', projectId]); // Invalidate chat queries
      queryClient.invalidateQueries(['user-projects']); // Remove from user's project list
      
      // Clear the project from store
      clearProject();
      
      // Always redirect to projects page after leaving
      // This provides clear feedback that the action succeeded
      setTimeout(() => {
        navigate('/projects', { replace: true });
      }, 500);
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to leave project';
      toast.error(errorMessage);
      console.error('[ProjectView] Leave project error:', error);
    }
  });

  // Update project mutation (for access control and publishing)
  const updateProjectMutation = useMutation({
    mutationFn: (data) => projectAPI.update(projectId, data),
    onSuccess: (response) => {
      const updatedProject = response.data?.project || response.data;
      
      // Update cache
      queryClient.setQueryData(createQueryKey.project(projectId), (oldData) => {
        if (!oldData) return oldData;
        if (updatedProject) {
          return {
            ...oldData,
            data: updatedProject
          };
        }
        return {
          ...oldData,
          data: {
            ...(oldData.data?.project || oldData.data),
            ...response.data
          }
        };
      });
      
      // Invalidate queries to ensure fresh data
      queryClient.invalidateQueries(createQueryKey.project(projectId));
      queryClient.invalidateQueries(['user-projects']);
      queryClient.invalidateQueries(['projects']);
    },
    onError: (error) => {
      toast.error('Failed to update project settings');
      console.error('[ProjectView] Update project error:', error);
    }
  });
  
  // Legacy event settings + waiting-room mutations removed.

  // Archive (finish) project mutation
  const archiveProjectMutation = useMutation({
    mutationFn: () => {
      const id = projectId || project?.id;
      if (!id) throw new Error('Missing projectId');
      
      // Only owners can archive
      if (!isOwner) {
        throw new Error('Only the project owner can archive this project');
      }
      
      return projectAPI.updateStatus(id, { status: 'finished' });
    },
    onSuccess: () => {
      const id = projectId || project?.id;
      
      // Invalidate all relevant queries to ensure fresh data
      queryClient.invalidateQueries(createQueryKey.project(id));
      // Invalidate ALL user-projects queries (all filter combinations)
      queryClient.invalidateQueries({ queryKey: ['user-projects'], exact: false });
      // Invalidate NewProjectPage queries to refresh /new page
      queryClient.invalidateQueries({ queryKey: ['new-page-live-projects'] });
      queryClient.invalidateQueries({ queryKey: ['new-page-ongoing-projects'] });
      queryClient.invalidateQueries(['project-chats', id]);
      queryClient.invalidateQueries(['chats', id]);
      
      // Also optimistically remove the project from Active tab cache
      // Update all user-projects queries to remove this project from active/pending lists
      queryClient.getQueryCache().findAll({ queryKey: ['user-projects'] }).forEach((query) => {
        queryClient.setQueryData(query.queryKey, (oldData) => {
          if (!oldData?.pages) return oldData;
          return {
            ...oldData,
            pages: oldData.pages.map(page => ({
              ...page,
              data: Array.isArray(page?.data) 
                ? page.data.filter(p => {
                    // Remove from active lists (ongoing/pending), but keep in finished lists
                    if (p?.id === id) {
                      const isActiveStatus = p.status === 'ongoing' || p.status === 'pending';
                      return !isActiveStatus; // Remove if it was active/pending
                    }
                    return true;
                  })
                : page.data
            }))
          };
        });
      });
      
      // Navigate back to projects page and switch to History tab to show archived project
      setIsMenuOpen(false);
      navigate('/projects?tab=history', { replace: true });
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to archive project';
      toast.error(errorMessage);
      console.error('[ProjectView] Archive project error:', error);
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => {
      const id = projectId || project?.id;
      if (!id) throw new Error('Missing projectId');
      
      // Double-check permissions before making the API call
      if (!canDelete) {
        throw new Error('You do not have permission to delete this project');
      }
      
      return projectAPI.delete(id);
    },
    onSuccess: () => {
      const id = projectId || project?.id;
      
      // Remove the project from cache immediately to prevent stale data
      // Handle infinite query structure properly for user-projects
      queryClient.setQueryData(['user-projects'], (oldData) => {
        if (!oldData?.pages) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map(page => ({
            ...page,
            data: Array.isArray(page?.data) ? page.data.filter(p => p?.id !== id) : page.data
          }))
        };
      });
      
      // Also update all user-projects queries with different filter combinations
      queryClient.getQueryCache().findAll({ queryKey: ['user-projects'] }).forEach((query) => {
        queryClient.setQueryData(query.queryKey, (oldData) => {
          if (!oldData?.pages) return oldData;
          return {
            ...oldData,
            pages: oldData.pages.map(page => ({
              ...page,
              data: Array.isArray(page?.data) ? page.data.filter(p => p?.id !== id) : page.data
            }))
          };
        });
      });
      
      // Update community-finished-projects cache (infinite query structure)
      queryClient.setQueryData(['community-finished-projects'], (oldData) => {
        if (!oldData?.pages) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map(page => {
            // Handle the nested structure: page.data.data.projects
            if (page?.data?.data?.projects) {
              return {
                ...page,
                data: {
                  ...page.data,
                  data: {
                    ...page.data.data,
                    projects: page.data.data.projects.filter(p => p?.id !== id)
                  }
                }
              };
            }
            // Fallback for other structures
            return {
              ...page,
              data: Array.isArray(page?.data) ? page.data.filter(p => p?.id !== id) : page.data
            };
          })
        };
      });
      
      // Remove the specific project query
      queryClient.removeQueries({ queryKey: createQueryKey.project(id) });
      queryClient.removeQueries({ queryKey: ['project', id] });
      
      // Invalidate all user-projects queries (with all filter combinations)
      queryClient.invalidateQueries({ queryKey: ['user-projects'] });
      queryClient.invalidateQueries({ queryKey: ['community-finished-projects'] });
      
      clearProject();
      navigate('/projects', { replace: true });
    },
    onError: (error) => {
      console.error('Delete error:', error);
      toast.error('Failed to delete project: ' + (error?.response?.data?.detail || error?.message || 'Unknown error'));
    }
  });

  // Load trail(s) - SECURITY: Only load current user's personal trail, never owner's trail
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        // Always try to get the current user's personal track first
        if (user?.id) {
          try {
            // Try to get the current user's track for this project
            const last = await trackAPI.getLast(projectId, chatId ? { chatId } : {});
            const tid = last?.data?.id || last?.data?.trackId;
            if (tid) {
              const res = await trackAPI.getTrack(tid, { simplified: false });
              const pts = res?.data?.points || [];
              if (pts.length > 1) {
                const path = pts.map(p => [p.lat, p.lng]);
                if (!cancelled) setReplayPath(path);
                return;
              }
            }
          } catch (e) {
            console.warn('Failed to load user track for project:', e);
          }
        }
        
        // SECURITY: No fallback to owner's trail - only show user's personal trail
        // If user has no track, show empty trail
        if (!cancelled) setReplayPath([]);
      } catch (e) {
        // ignore
      }
    };
    load();
    return () => { cancelled = true; };
  }, [projectId, user?.id, chatId]);
  
  // Message pins hook for photo pins on finished project maps
  const { clusteredPins: messagePins, shouldShowPins: showMessagePins } = useMessagePins([], {
    enabledTypes: ['PHOTO'], // Show photo pins only
    clustering: true,
    showInSidebar: !!project && project.status === 'finished' // Show pins for finished projects
  });
  // For site projects, trails are noise (multiple sessions/users). Keep them hidden.
  const shouldShowTrail = useMemo(() => false, []);
  

  const handleClose = () => {
    // Clear the active project state to prevent showing finished project again
    clearProject();
    // Navigate back based on previousPath in location state, or showBackLink prop, otherwise go to profile
    const prev = location.state?.previousPath;
    
    // If we have a previous path, navigate back to it
    if (prev || showBackLink) {
      // Fix for context-aware back navigation (e.g. from /discover)
      // Handle both exact match and potential sub-paths/trailing slashes
      const isCommunity = prev && (prev === '/discover' || prev.startsWith('/discover'));
      const targetPath = isCommunity ? '/discover' : '/projects';
      
      navigate(targetPath, { 
        replace: true,
        state: {
          transitionDirection: 'back',
          previousPath: `/projects/${projectId}`
        }
      });
    } else {
      navigate('/profile', { replace: true });
    }
  };

  const handleCopyShareLink = async () => {
    if (!shareUrl) return;

    try {
      await copyTextToClipboard(
        buildProjectShareClipboardText({
          projectName: project?.name,
          url: shareUrl,
        })
      );
      toast.success('Share text copied to clipboard');
    } catch (_) {
      toast.error('Could not copy link');
    }
  };

  const handleShareClick = async () => {
    if (!isPublished || !shareUrl) return;

    try {
      const nativeShareResult = await shareProjectNatively({
        projectName: project?.name,
        url: shareUrl,
      });

      if (nativeShareResult !== 'unsupported') {
        return;
      }
    } catch (_) {
      toast.error('Could not open share options');
      return;
    }

    setIsShareModalOpen(true);
  };


  // Fetch project images (single page is enough for header/grid here)
  // Use source: 'all' to include AI-generated images as fallback when no user uploads exist
  const { data: projectImagesResp, isSuccess: imagesSuccess, isError: imagesError } = useQuery({
    queryKey: ['finished-project-images', projectId],
    queryFn: () => projectId ? projectAPI.getImages(projectId, { limit: 100, sort: 'latest', source: 'all' }) : Promise.resolve({ data: { data: { images: [] } } }),
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000
  });

  const displayImages = useMemo(() => {
    const settled = imagesSuccess || imagesError;
    if (!settled) {
      // Avoid rendering any images until the API resolves to prevent flicker
      return [];
    }
    try {
      const list = projectImagesResp?.data?.data?.images || [];
      const seen = new Set();
      const images = [];
      for (const img of list) {
        const url = typeof img === 'string' ? img : img?.url;
        if (!url || seen.has(url)) continue;
        seen.add(url);
        images.push(typeof img === 'string' ? { url: img, takenBy: null } : img);
      }
      if (images.length > 0) return images;
    } catch (_) {}
    // Fallback to legacy project.images if API returns empty or errors
    try {
      const pimgs = Array.isArray(project?.images) ? project.images.map(img => ({ url: getImageUrl(img), takenBy: null })) : [];
      return pimgs;
    } catch (_) {
      return [];
    }
  }, [projectImagesResp, project?.images, imagesSuccess, imagesError]);
  const sharePreviewImageUrl = useMemo(() => {
    const firstImageUrl = displayImages?.[0]?.url;
    return firstImageUrl ? getImageUrl(firstImageUrl) : '';
  }, [displayImages]);


  // Scroll detection for mobile header background - using IntersectionObserver
  const imagesQuerySettled = imagesSuccess || imagesError;
  
  useEffect(() => {
    // If there are no images, show scrolled state immediately (no hero image to scroll past)
    if (imagesQuerySettled && (!displayImages || displayImages.length === 0)) {
      setIsScrolledPastImage(true);
      return;
    }

    const observerCallback = (entries) => {
      const [entry] = entries;
      setIsScrolledPastImage(!entry.isIntersecting);
    };

    const observer = new IntersectionObserver(observerCallback, {
      threshold: 0.3,
      rootMargin: '0px'
    });

    const currentRef = mobileHeroRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
      observer.disconnect();
    };
  }, [displayImages, imagesQuerySettled]);

  // Fetch original explore project (if any)
  const { data: originalResp } = useQuery({
    queryKey: ['original-project', projectId, project?.originalProjectId],
    queryFn: async () => {
      if (!projectId || !project?.originalProjectId) return { data: null };
      try {
        return await projectAPI.getOriginal(projectId);
      } catch (err) {
        // Swallow 404 (not a clone) to avoid retries/log spam in UI
        if (err?.response?.status === 404) {
          return { data: null };
        }
        throw err;
      }
    },
    enabled: !!projectId && !!project?.originalProjectId,
    staleTime: 5 * 60 * 1000,
    retry: false
  });

  const originalProject = useMemo(() => {
    // Backend returns { data: { original: {...} } }
    const p = originalResp?.data?.data?.original || originalResp?.data?.original || originalResp?.data;
    return p && p.id ? p : null;
  }, [originalResp]);

  // Reviews are saved to the instance (projectId), not the template
  // This ensures privacy: users only see reviews for their specific project instance
  const reviewProjectId = projectId;

  // Check if the current user already left a review for this project instance
  const { data: myReviewResp } = useQuery({
    queryKey: createQueryKey.myReview(reviewProjectId),
    queryFn: () => reviewAPI.getMy(reviewProjectId),
    enabled: !!reviewProjectId && !!user?.id,
    staleTime: 5 * 60 * 1000
  });

  const hasMyReview = useMemo(() => {
    return Boolean(myReviewResp?.data?.data?.review || myReviewResp?.data?.review);
  }, [myReviewResp]);

  const initialVisitedAt = useMemo(() => {
    try {
      const raw = project?.updatedAt;
      const d = raw ? new Date(raw) : new Date();
      if (isNaN(d.getTime())) return new Date().toISOString().slice(0, 10);
      return d.toISOString().slice(0, 10);
    } catch (_) {
      return new Date().toISOString().slice(0, 10);
    }
  }, [project?.updatedAt]);

  // Auto-open review prompt once for owners and participants when the project finishes
  // Also opens when a chat session ends (for Site projects)
  useEffect(() => {
    const viewerId = user?.id || user?._id;
    if (!viewerId || !reviewProjectId) {
      lastStatusRef.current = project?.status;
      return;
    }
    const prev = lastStatusRef.current;
    const curr = project?.status;
    const justFinished = prev !== 'finished' && curr === 'finished';
    const sessionEnded = location.state?.sessionEnded === true;
    const endedChatId = location.state?.endedChatId;
    const shouldAttemptPrompt = (openReviewOnMount || justFinished || sessionEnded) && isViewerParticipantOrOwner;
    
    // Back button behavior when coming from project completion or session end
    if (openReviewOnMount || justFinished || sessionEnded) {
      // Store the current URL before modifying history
      const currentUrl = window.location.href;
      
      // Add home page to history stack
      window.history.pushState(null, '', '/');
      
      // Immediately restore the current URL without triggering navigation
      window.history.pushState(null, '', currentUrl);
    }

    const key = `reviewPromptSeen:${viewerId}:${reviewProjectId}`;
    let seen = false;
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        seen = window.localStorage.getItem(key) === '1';
      }
    } catch (_) {}

    // For testing: Clear the seen flag if openReviewOnMount is true
    // Also clear if session ended (to allow review prompt after each session)
    if ((openReviewOnMount || sessionEnded) && seen) {
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.removeItem(key);
          seen = false;
        }
      } catch (_) {}
    }

    // For session-level reviews, check if THIS specific session was already reviewed
    // (not just if the project has any review)
    const hasReviewForThisSession = sessionEnded && endedChatId 
      ? Boolean(sessionReviewsByChatId[endedChatId])
      : false;
    
    // For session reviews, only block if this specific session was reviewed
    // For project reviews (no endedChatId), use the project-level hasMyReview check
    const shouldBlockForExistingReview = (sessionEnded && endedChatId) 
      ? hasReviewForThisSession 
      : hasMyReview;

    if (shouldAttemptPrompt && !seen && !shouldBlockForExistingReview) {
      // If coming from a session that just ended, set the chat ID for the review
      // This ensures the review is linked to the specific session
      if (sessionEnded && endedChatId) {
        setReviewingSessionId(endedChatId);
        // Title will be set from chats if available, fallback to generic
        setReviewingSessionTitle('Session');
      }
      setIsReviewOpen(true);
    }

    lastStatusRef.current = curr;
    // Note: We intentionally don't include location.state?.endedChatId in deps
    // as we only want to react to sessionEnded, not chatId changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.status, openReviewOnMount, location.state?.sessionEnded, user?.id, user?._id, reviewProjectId, hasMyReview, isViewerParticipantOrOwner, sessionReviewsByChatId]);

  // Format date display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Date formatting - different for finished vs ongoing projects
  const completedDateRaw = project.updatedAt;
  const completedDate = completedDateRaw ? formatDate(completedDateRaw) : 'Unknown';

  // Nicely formatted game mode label (e.g., "endless" => "Endless")
  // State for mobile image carousel
  const [currentImageIndex] = useState(0);

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-900 overflow-y-auto mobile-scroll relative">
      {/* Mobile Fixed Top Controls - TWO BARS: one for over-image, one for scrolled */}
      
      {/* Bar 1: Always visible when NOT scrolled past image (transparent, dark buttons) */}
      <div 
        className={`md:hidden fixed top-0 left-0 right-0 z-[2000] bg-transparent ${
          isScrolledPastImage ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
        style={{ paddingTop: 'var(--app-safe-area-top)' }}
      >
        <div className="h-14 flex items-center justify-between px-4">
          <button
            onClick={handleClose}
            className="p-2 rounded-full bg-black/40 text-white backdrop-blur-sm"
          >
            <ChevronLeftIcon className="w-6 h-6" />
          </button>
          <div className="flex gap-2">
            <button
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                setMenuPosition({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
                setIsMenuOpen((v) => !v);
              }}
              className="p-2 rounded-full bg-black/40 text-white backdrop-blur-sm"
            >
              <EllipsisHorizontalIcon className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>
      
      {/* Bar 2: Only visible when scrolled past image (white bg, title, light buttons) */}
      {isScrolledPastImage && (
        <div 
          className="md:hidden fixed top-0 left-0 right-0 z-[2000] bg-white/90 dark:bg-gray-900/90 shadow-sm backdrop-blur-sm animate-fade-in"
          style={{ paddingTop: 'var(--app-safe-area-top)' }}
        >
          <div className="h-14 flex items-center justify-between px-4">
            <button
              onClick={handleClose}
              className="p-2 rounded-full text-gray-700 dark:text-gray-200"
            >
              <ChevronLeftIcon className="w-6 h-6" />
            </button>
            
            <span className="font-semibold text-gray-900 dark:text-white truncate max-w-[60%]">
              {project.name}
            </span>

            <div className="flex gap-2">
              <button
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  setMenuPosition({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
                  setIsMenuOpen((v) => !v);
                }}
                className="p-2 rounded-full text-gray-700 dark:text-gray-200"
              >
                <EllipsisHorizontalIcon className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Menu Dropdown (Portal to Body for Z-Index/Overflow safety) */}
      {createPortal(
        <AnimatePresence>
          {isMenuOpen && (
            <>
              <div 
                className="fixed inset-0 z-[60000]" 
                onClick={() => setIsMenuOpen(false)} 
              />
              <motion.div 
                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                style={{ top: menuPosition.top, right: menuPosition.right }}
                className="fixed z-[60001] w-48 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-xl overflow-hidden origin-top-right"
              >
                {isPublished && (
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      handleShareClick();
                    }}
                    className="w-full text-left px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 flex items-center gap-2"
                  >
                    <LinkIcon className="w-4 h-4" />
                    Share
                  </button>
                )}
                {canReportProject && (
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      setIsReportModalOpen(true);
                    }}
                    className="w-full text-left px-4 py-3 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                  >
                    <Flag className="w-4 h-4" />
                    Report
                  </button>
                )}
                {isCollaborator && (
                  <button
                    onClick={() => {
                      const confirmMessage = project?.isPublic
                        ? 'Are you sure you want to leave this project? You can still view it, but won\'t be able to participate.'
                        : 'Are you sure you want to leave this project? You will no longer have access to it.';
                      if (window.confirm(confirmMessage)) {
                        leaveProjectMutation.mutate();
                      }
                      setIsMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                  >
                    <LogOut className="w-4 h-4" />
                    Leave Project
                  </button>
                )}
                {canDelete && (
                  <button
                    onClick={() => {
                      setShowEditModal(true);
                      setIsMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 flex items-center gap-2"
                  >
                    <Settings className="w-4 h-4" />
                    Edit Project
                  </button>
                )}
                {isOwner && project?.status !== 'finished' && (
                  <button
                    onClick={() => {
                      setShowArchiveConfirm(true);
                      setIsMenuOpen(false);
                    }}
                    disabled={archiveProjectMutation.isPending}
                    className="w-full text-left px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Archive className="w-4 h-4" />
                    {archiveProjectMutation.isPending ? 'Archiving...' : 'Archive Project'}
                  </button>
                )}
                {canDelete && (
                  <button
                    onClick={() => { setShowDeleteConfirm(true); setIsMenuOpen(false); }}
                    className="w-full text-left px-4 py-3 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete project
                  </button>
                )}
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* ===== MOBILE: Single Image with Counter ===== */}
      <div className="md:hidden" ref={mobileHeroRef}>
        <div className="relative aspect-[4/3] bg-gray-100 dark:bg-gray-800">
          {!imagesQuerySettled ? (
            /* Loading skeleton while fetching images */
            <div className="w-full h-full bg-gray-200 dark:bg-gray-700 animate-pulse" />
          ) : displayImages && displayImages.length > 0 ? (
            <>
              <img
                src={getImageUrl(displayImages[currentImageIndex]?.url)}
                alt={project.name}
                className="w-full h-full object-cover cursor-pointer"
                onClick={() => navigate(`/projects/${projectId}/images`)}
              />
              {/* Image Counter Badge */}
              {displayImages.length > 1 && (
                <div className="absolute bottom-4 right-4 px-3 py-1.5 bg-gray-900/80 backdrop-blur-sm text-white text-sm font-medium rounded-lg">
                  {currentImageIndex + 1} / {displayImages.length}
                </div>
              )}
            </>
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-emerald-600 to-teal-700 flex items-center justify-center">
              {/* <div className="text-white/30 text-6xl">🌿</div> */}
            </div>
          )}
        </div>
        
        {/* Mobile Title Section */}
        <div className="px-6 pt-5 pb-0">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white leading-tight">
            {project.name}
          </h1>
        </div>
      </div>

      {/* ===== DESKTOP: Title Header + Image Grid (Airbnb Style) ===== */}
      <div className="hidden md:block">
        {/* Desktop Header with Title */}
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-6 pb-6">
          {/* Back Link */}
          {showBackLink && (
            <Link 
              to={(location.state?.previousPath === '/discover' || location.state?.previousPath?.startsWith('/discover')) ? '/discover' : '/projects'} 
              state={{
                transitionDirection: 'back',
                previousPath: `/projects/${projectId}`
              }}
              className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-4 transition-colors"
            >
              <ChevronLeftIcon className="h-4 w-4 mr-1" />
              {(location.state?.previousPath === '/discover' || location.state?.previousPath?.startsWith('/discover')) ? 'Back to Discover' : 'Back to Projects'}
            </Link>
          )}
          
          {/* Title Row */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h1 className="text-3xl font-semibold text-gray-900 dark:text-white leading-tight">
                {project.name}
              </h1>
            </div>
            
            {/* Action Buttons */}
            <div className="flex items-center gap-1">
              {/* <button 
                onClick={handleShareClick} 
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <ShareIcon className="w-4 h-4" />
                <span className="hidden lg:inline">Share</span>
              </button> */}
              <button
                ref={menuButtonRef}
                onClick={() => {
                  if (menuButtonRef.current) {
                    const rect = menuButtonRef.current.getBoundingClientRect();
                    setMenuPosition({
                      top: rect.bottom + 8,
                      right: window.innerWidth - rect.right
                    });
                  }
                  setIsMenuOpen((v) => !v);
                }}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <EllipsisHorizontalIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Desktop Image Grid - Adapts based on number of images */}
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pb-4">
          <div 
            className="relative rounded-xl overflow-hidden cursor-pointer group"
            onClick={() => navigate(`/projects/${projectId}/images`)}
          >
            {displayImages && displayImages.length >= 5 ? (
              /* 5+ images: Full grid layout (1 large left, 4 small right) */
              <div className="grid grid-cols-4 grid-rows-2 gap-2 h-[400px]">
                <div className="col-span-2 row-span-2 relative overflow-hidden rounded-l-xl">
                  <img src={getImageUrl(displayImages[0]?.url)} alt={project.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                </div>
                <div className="relative overflow-hidden">
                  <img src={getImageUrl(displayImages[1]?.url)} alt="" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                </div>
                <div className="relative overflow-hidden rounded-tr-xl">
                  <img src={getImageUrl(displayImages[2]?.url)} alt="" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                </div>
                <div className="relative overflow-hidden">
                  <img src={getImageUrl(displayImages[3]?.url)} alt="" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                </div>
                <div className="relative overflow-hidden rounded-br-xl">
                  <img src={getImageUrl(displayImages[4]?.url)} alt="" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate(`/projects/${projectId}/images`); }}
                    className="absolute bottom-6 right-6 px-4 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors shadow-sm flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 16 16">
                      <circle cx="2" cy="2" r="1.5" /><circle cx="8" cy="2" r="1.5" /><circle cx="14" cy="2" r="1.5" />
                      <circle cx="2" cy="8" r="1.5" /><circle cx="8" cy="8" r="1.5" /><circle cx="14" cy="8" r="1.5" />
                      <circle cx="2" cy="14" r="1.5" /><circle cx="8" cy="14" r="1.5" /><circle cx="14" cy="14" r="1.5" />
                    </svg>
                    Show all photos
                  </button>
                </div>
              </div>
            ) : displayImages && displayImages.length === 4 ? (
              /* 4 images: 1 large left, 3 right (2 top, 1 bottom full width) */
              <div className="grid grid-cols-4 grid-rows-2 gap-2 h-[400px]">
                <div className="col-span-2 row-span-2 relative overflow-hidden rounded-l-xl">
                  <img src={getImageUrl(displayImages[0]?.url)} alt={project.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                </div>
                <div className="relative overflow-hidden">
                  <img src={getImageUrl(displayImages[1]?.url)} alt="" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                </div>
                <div className="relative overflow-hidden rounded-tr-xl">
                  <img src={getImageUrl(displayImages[2]?.url)} alt="" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                </div>
                <div className="col-span-2 relative overflow-hidden rounded-br-xl">
                  <img src={getImageUrl(displayImages[3]?.url)} alt="" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate(`/projects/${projectId}/images`); }}
                    className="absolute bottom-3 right-3 px-4 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors shadow-sm flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 16 16">
                      <circle cx="2" cy="2" r="1.5" /><circle cx="8" cy="2" r="1.5" /><circle cx="14" cy="2" r="1.5" />
                      <circle cx="2" cy="8" r="1.5" /><circle cx="8" cy="8" r="1.5" /><circle cx="14" cy="8" r="1.5" />
                      <circle cx="2" cy="14" r="1.5" /><circle cx="8" cy="14" r="1.5" /><circle cx="14" cy="14" r="1.5" />
                    </svg>
                    Show all photos
                  </button>
                </div>
              </div>
            ) : displayImages && displayImages.length === 3 ? (
              /* 3 images: 1 large left, 2 stacked right */
              <div className="grid grid-cols-3 grid-rows-2 gap-2 h-[400px]">
                <div className="col-span-2 row-span-2 relative overflow-hidden rounded-l-xl">
                  <img src={getImageUrl(displayImages[0]?.url)} alt={project.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                </div>
                <div className="relative overflow-hidden rounded-tr-xl">
                  <img src={getImageUrl(displayImages[1]?.url)} alt="" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                </div>
                <div className="relative overflow-hidden rounded-br-xl">
                  <img src={getImageUrl(displayImages[2]?.url)} alt="" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate(`/projects/${projectId}/images`); }}
                    className="absolute bottom-3 right-3 px-4 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors shadow-sm flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 16 16">
                      <circle cx="2" cy="2" r="1.5" /><circle cx="8" cy="2" r="1.5" /><circle cx="14" cy="2" r="1.5" />
                      <circle cx="2" cy="8" r="1.5" /><circle cx="8" cy="8" r="1.5" /><circle cx="14" cy="8" r="1.5" />
                      <circle cx="2" cy="14" r="1.5" /><circle cx="8" cy="14" r="1.5" /><circle cx="14" cy="14" r="1.5" />
                    </svg>
                    Show all photos
                  </button>
                </div>
              </div>
            ) : displayImages && displayImages.length === 2 ? (
              /* 2 images: Side by side, equal width */
              <div className="grid grid-cols-2 gap-2 h-[400px]">
                <div className="relative overflow-hidden rounded-l-xl">
                  <img src={getImageUrl(displayImages[0]?.url)} alt={project.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                </div>
                <div className="relative overflow-hidden rounded-r-xl">
                  <img src={getImageUrl(displayImages[1]?.url)} alt="" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate(`/projects/${projectId}/images`); }}
                    className="absolute bottom-3 right-3 px-4 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors shadow-sm flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 16 16">
                      <circle cx="2" cy="2" r="1.5" /><circle cx="8" cy="2" r="1.5" /><circle cx="14" cy="2" r="1.5" />
                      <circle cx="2" cy="8" r="1.5" /><circle cx="8" cy="8" r="1.5" /><circle cx="14" cy="8" r="1.5" />
                      <circle cx="2" cy="14" r="1.5" /><circle cx="8" cy="14" r="1.5" /><circle cx="14" cy="14" r="1.5" />
                    </svg>
                    Show all photos
                  </button>
                </div>
              </div>
            ) : displayImages && displayImages.length === 1 ? (
              /* 1 image: Full width with fixed height */
              <div className="relative overflow-hidden rounded-xl w-full h-[400px]">
                <img src={getImageUrl(displayImages[0]?.url)} alt={project.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
              </div>
            ) : (
              /* No Images Placeholder */
              <div className="h-[300px] bg-gradient-to-br from-emerald-600 to-teal-700 rounded-xl flex items-center justify-center">
                {/* <div className="text-white/30 text-8xl">🌿</div> */}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sticky Tabs */}
      <div className="top-0 md:top-0 z-30 border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-6 overflow-x-auto scrollbar-hide">
            {tabItems.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`relative py-4 text-sm md:text-base font-medium transition-colors whitespace-nowrap flex items-center gap-2 ${
                  activeTab === tab.id 
                    ? 'text-blue-600 dark:text-blue-400' 
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
              >
                {tab.icon && <tab.icon className="w-4 h-4" />}
                {tab.label}
                {activeTab === tab.id && (
                  <motion.div 
                    layoutId="activeTab"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400 rounded-full"
                  />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto w-full px-6 lg:px-8 pt-0 pb-32 pt-4 md:pb-8">
        
        {/* ACTION BANNERS */}
        {/* Event/waiting-room banners removed in simplified model */}

        {/* Tab Content */}
        {activeTab === 'summary' && (
            <div className="space-y-10 pb-8">
              {/* End-of-tour souvenir (workstream S): once every objective is
                  checked off — or the visitor explicitly ended a partial run —
                  the overview opens with a recap the visitor can share as a
                  postcard. It embeds the venue link, so the plain backlink
                  below is skipped to avoid saying it twice. */}
              {tourCompleted || tourEnded ? (
                <TourRecapHero project={project} images={displayImages} />
              ) : (
                /* Venue backlink — sessions born from a partner starter point back
                    to the venue's public page instead of dead-ending here */
                <VenueBacklink originalProjectId={project?.originalProjectId} />
              )}

              {/* Start Session Card (Site Projects) - Entire card is clickable */}
              {isSiteProject && canStartOrResumeSession && (
                <div 
                  onClick={() => {
                    if (activeChat) {
                      navigate(`/projects/${projectId}/chats/${activeChat.id}`);
                    } else if (!newSessionMutation.isLoading) {
                      newSessionMutation.mutate();
                    }
                  }}
                  className="py-5 px-6 rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg cursor-pointer hover:shadow-xl transition-all hover:scale-[1.01] active:scale-[0.99]"
                >
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm animate-pulse-slow">
                        {activeChat ? <Play className="w-8 h-8 fill-current" /> : <Play className="w-6 h-6" />}
                      </div>
                      <div>
                        <h3 className="font-bold text-xl">{activeChat ? 'Session in Progress' : 'Ready to Start?'}</h3>
                        <p className="text-blue-100 text-sm max-w-md">
                          {activeChat 
                            ? `You have an active session from ${new Date(activeChat.createdAt).toLocaleDateString()}.` 
                            : 'Start a new session to contribute to this project.'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex flex-col gap-3 w-full sm:w-auto min-w-[200px]">
                      {activeChat ? (
                        <div
                          onClick={(e) => e.stopPropagation()}
                          className="w-full px-6 py-3.5 bg-white text-blue-600 font-bold text-base lg:text-xl rounded-lg shadow-lg flex items-center justify-center gap-2 pointer-events-none"
                        >
                          <Play className="w-4 h-4 fill-current" />
                          Resume Session
                        </div>
                      ) : (
                        <div
                          onClick={(e) => e.stopPropagation()}
                          className={`w-full px-6 py-3.5 bg-white text-blue-600 font-bold text-base lg:text-xl rounded-lg shadow-lg flex items-center justify-center gap-2 pointer-events-none ${newSessionMutation.isLoading ? 'opacity-70' : ''}`}
                        >
                          <Play className="w-4 h-4 fill-current" />
                          {newSessionMutation.isLoading ? 'Starting...' : 'Start Session'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Event settings removed in simplified model */}

              {/* Objectives */}
              {(project?.objectives?.length > 0 || isOwner) && (
                <section>
                  <ObjectivesList 
                    project={project} 
                    currentUser={user}
                    showHeader={true}
                    showAddButton={true}
                  />
                </section>
              )}

              {/* About Section */}
              {(project.description || isOwner || isReadOnlyViewer || (!isSiteProject && completedDate)) && (
                <section>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                      About
                    </h3>
                    {!isSiteProject && completedDate && (
                      <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
                        <Calendar className="w-4 h-4" />
                        <span>{completedDate}</span>
                      </div>
                    )}
                  </div>
                  
                  {project.description && (
                    <p className="text-gray-700 dark:text-gray-200 leading-relaxed whitespace-pre-wrap mb-6 text-serif">
                      {project.description}
                    </p>
                  )}
                  
                  {/* Publish Status - Owner */}
                  {isOwner && (
                    <div className="mt-2 p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                          isPublished 
                            ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' 
                            : 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'
                        }`}>
                          {isPublished ? <Globe className="w-5 h-5" /> : <Lock className="w-5 h-5" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                            {isPublished ? 'Published' : 'Members-Only'}
                          </h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {isPublished ? 'Others can follow along' : 'Only visible to your team'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => updateProjectMutation.mutate({ isPublic: !isPublished })}
                        disabled={updateProjectMutation.isLoading}
                        className={`flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg font-semibold text-sm transition-all w-full sm:w-auto ${
                          isPublished
                            ? 'bg-white text-gray-700 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        } disabled:opacity-50`}
                      >
                        {isPublished ? <Lock className="w-3.5 h-3.5" /> : <Globe className="w-3.5 h-3.5" />}
                        {updateProjectMutation.isLoading ? 'Saving...' : isPublished ? 'Unpublish' : 'Publish'}
                      </button>
                    </div>
                  )}
                  
                  {/* Read-only viewer notice */}
                  {isReadOnlyViewer && (
                    <div className="mt-2 p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center gap-4">
                        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                          <Globe className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Viewing Mode</div>
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Published Journal</h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            This quest is invite-only. Browse the public journal to follow along.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Template Link */}
                  {originalProject && (
                    <div className="mt-2">
                      <div 
                        onClick={() => navigate(`/starters/${originalProject.id}`)}
                        className="group p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600 transition-all"
                      >
                        <div className="flex items-center gap-4">
                          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700/50 flex items-center justify-center group-hover:bg-gray-200 dark:group-hover:bg-gray-700 transition-colors">
                            <LinkIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Based on Template</div>
                            <h4 className="text-xs font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors truncate">
                              {originalProject.name}
                            </h4>
                          </div>
                          <ChevronRight className="w-5 h-5 text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-400 transition-colors flex-shrink-0" />
                        </div>
                      </div>
                    </div>
                  )}

                </section>
              )}

              
            </div>
          )}

        {activeTab === 'members' && (
          <section className="space-y-6">
              {/* Members Header */}
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Team members
                </h3>
                {isOwner && (
                  <button 
                    onClick={() => setShowInviteModal(true)}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors flex items-center gap-2"
                  >
                    <UserPlus className="w-4 h-4" /> Invite
                  </button>
                )}
              </div>
              
              {/* Members List */}
              <div className="space-y-1">
                {isLoadingParticipants ? (
                  <div className="py-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Loading members...</p>
                  </div>
                ) : allMembers.map((member) => {
                  const isMe = user?.id === member.id;
                  const isProjOwner = project?.ownerId === member.id;
                  
                  // Helper function to get initials
                  const getInitials = (m) => {
                    if (m.firstName && m.lastName) {
                      return `${m.firstName[0]}${m.lastName[0]}`.toUpperCase();
                    } else if (m.firstName) {
                      return m.firstName[0].toUpperCase();
                    }
                    return m.username?.[0]?.toUpperCase() || 'U';
                  };
                  
                  const hasAvatar = member.avatar && member.avatar.trim();
                  
                  return (
                    <div key={member.id} className="py-4 flex items-center justify-between border-b border-gray-100 dark:border-gray-800 last:border-0">
                      <Link 
                        to={`/users/${member.username}`}
                        className="flex items-center gap-4 flex-1 hover:opacity-80 transition-opacity"
                      >
                        {/* Avatar with placeholder */}
                        <div className="w-12 h-12 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {hasAvatar ? (
                            <img 
                              src={getImageUrl(member.avatar)} 
                              className="w-12 h-12 rounded-full object-cover" 
                              alt=""
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.parentElement.querySelector('.avatar-initials')?.classList.remove('hidden');
                              }}
                            />
                          ) : null}
                          <span className={`text-white font-bold text-sm ${hasAvatar ? 'hidden avatar-initials' : ''}`}>
                            {getInitials(member)}
                          </span>
                        </div>
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                            {member.username}
                            {isProjOwner && <span className="text-[10px] bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 px-1.5 py-0.5 rounded font-semibold">OWNER</span>}
                            {isMe && <span className="text-[10px] bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-1.5 py-0.5 rounded font-semibold">YOU</span>}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">{member.email}</div>
                        </div>
                      </Link>
                      {isMe && !isProjOwner && (
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            const confirmMessage = project?.isPublic
                              ? 'Are you sure you want to leave this project? You can still view it, but won\'t be able to participate.'
                              : 'Are you sure you want to leave this project? You will no longer have access to it.';
                            if (window.confirm(confirmMessage)) {
                              leaveProjectMutation.mutate();
                            }
                          }}
                          disabled={leaveProjectMutation.isPending}
                          className="px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors ml-2 flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <LogOut className="w-4 h-4" />
                          <span>Leave</span>
                        </button>
                      )}
                      {isOwner && !isProjOwner && !isMe && (
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            removeCollaboratorMutation.mutate(member.id);
                          }}
                          className="p-2 text-red-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors ml-2"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    );
                  })}
              </div>
            </section>
          )}

        {activeTab === 'chat' && (
          <section className="space-y-6">
            {/* Session History Header + New Session Button */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Session history
              </h3>
              {!isFinished && (
                <button
                  onClick={() => {
                    if (activeChat) {
                      if (window.confirm('Start a new session? Your current session will be saved.')) {
                        newSessionMutation.mutate();
                      }
                    } else {
                      newSessionMutation.mutate();
                    }
                  }}
                  disabled={newSessionMutation.isLoading}
                  className="w-full sm:w-auto px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-sm text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 shadow-sm"
                >
                  <Plus className="w-4 h-4" />
                  <span>New Session</span>
                </button>
              )}
            </div>
            
            {/* Sessions List */}
            <div className="space-y-3">
              {isLoadingChats ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
                </div>
              ) : historyChats.length > 0 ? (
                historyChats.map(chat => {
                  const sessionReview = sessionReviewsByChatId[chat.id];
                  const isArchived = !chat.isActive && chat.archivedAt;
                  const isMySession = String(chat.userId) === String(user?.id);
                  const sessionOwner = chat.user || {};
                  
                  return (
                    <div 
                      key={chat.id}
                      className={`rounded-xl border shadow-sm overflow-hidden transition-all ${
                        chat.isActive 
                          ? 'bg-blue-50/50 dark:bg-gray-800 border-blue-200 dark:border-blue-800 ring-1 ring-blue-100 dark:ring-blue-900' 
                          : isMySession
                            ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                            : 'bg-gray-50/50 dark:bg-gray-800/50 border-gray-100 dark:border-gray-700/50'
                      }`}
                    >
                      {/* Main Session Row - Clickable */}
                      <div 
                        onClick={() => navigate(`/projects/${projectId}/chats/${chat.id}`)}
                        className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors flex justify-between items-center group"
                      >
                        <div className="flex items-center gap-4">
                          {/* Avatar with ownership indicator */}
                          <div className="relative flex-shrink-0">
                            {chat.isActive ? (
                              <div className="w-10 h-10 rounded-full bg-blue-600 shadow-lg shadow-blue-600/20 flex items-center justify-center animate-pulse-slow">
                                <Play className="w-4 h-4 text-white fill-current" />
                              </div>
                            ) : sessionOwner.avatar ? (
                              <img 
                                src={getImageUrl(sessionOwner.avatar)} 
                                alt="" 
                                className={`w-10 h-10 rounded-full object-cover ${isMySession ? 'ring-2 ring-blue-500' : ''}`}
                              />
                            ) : (
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                                isMySession 
                                  ? 'bg-gradient-to-br from-blue-500 to-indigo-600' 
                                  : 'bg-gray-400 dark:bg-gray-600'
                              }`}>
                                {(sessionOwner.username?.[0] || chat.title?.[0] || 'S').toUpperCase()}
                              </div>
                            )}
                            {/* "You" checkmark badge */}
                            {isMySession && !chat.isActive && (
                              <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-blue-500 rounded-full border-2 border-white dark:border-slate-800 flex items-center justify-center">
                                <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              </div>
                            )}
                          </div>
                          
                          <div>
                            <div className={`font-bold transition-colors ${
                              chat.isActive 
                                ? 'text-blue-700 dark:text-blue-400' 
                                : isMySession
                                  ? 'text-gray-900 dark:text-white group-hover:text-blue-600'
                                  : 'text-gray-600 dark:text-gray-300'
                            }`}>
                              {chat.title || 'Untitled Session'}
                            </div>
                            <div className="text-xs text-gray-500 flex items-center gap-2 flex-wrap">
                              {/* Always show username - "You" for own sessions, @username for others */}
                              <span className={`font-medium ${isMySession ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'}`}>
                                {isMySession ? 'You' : (sessionOwner.username ? `@${sessionOwner.username}` : 'Unknown')}
                              </span>
                              <span>•</span>
                              <span>{new Date(chat.createdAt).toLocaleDateString()}</span>
                              <span>•</span>
                              <span>{new Date(chat.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                              {chat.isActive && (
                                <span className="px-2 py-0.5 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full font-bold flex items-center gap-1">
                                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                                  Active
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <ArrowRight className={`w-5 h-5 transition-all flex-shrink-0 ${
                          chat.isActive 
                            ? 'text-blue-500 translate-x-1' 
                            : 'text-gray-300 group-hover:text-blue-500 group-hover:translate-x-1'
                        }`} />
                      </div>
                      
                      {/* Session Review Row - Only for archived sessions owned by current user */}
                      {isArchived && isMySession && (
                        <div className="px-4 pb-4 pt-0">
                          <div className="border-t border-gray-100 dark:border-gray-700 pt-3">
                            {sessionReview ? (
                              /* Has Review - Show full review details */
                              <div className="space-y-2">
                                {/* Rating + Edit button row */}
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className="flex gap-0.5">
                                      {[1,2,3,4,5].map(i => (
                                        <svg 
                                          key={i} 
                                          className={`w-4 h-4 fill-current ${i <= sessionReview.rating ? 'text-yellow-500' : 'text-gray-300 dark:text-gray-500'}`} 
                                          viewBox="0 0 20 20"
                                        >
                                          <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                                        </svg>
                                      ))}
                                    </div>
                                    {/* Difficulty badge */}
                                    {sessionReview.difficulty && (
                                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                        sessionReview.difficulty === 'easy' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                        sessionReview.difficulty === 'moderate' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                                        sessionReview.difficulty === 'hard' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                                        'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                      }`}>
                                        {sessionReview.difficulty.charAt(0).toUpperCase() + sessionReview.difficulty.slice(1)}
                                      </span>
                                    )}
                                  </div>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingReview(sessionReview);
                                      setReviewingSessionId(chat.id);
                                      setReviewingSessionTitle(chat.title || 'Untitled Session');
                                      setIsReviewOpen(true);
                                    }}
                                    className="text-xs text-blue-600 dark:text-blue-400 font-medium hover:underline"
                                  >
                                    Edit
                                  </button>
                                </div>
                                
                                {/* Tags */}
                                {sessionReview.tags && sessionReview.tags.filter(t => !t.startsWith('_')).length > 0 && (
                                  <div className="flex flex-wrap gap-1">
                                    {sessionReview.tags.filter(t => !t.startsWith('_')).map((tag, i) => (
                                      <span 
                                        key={i} 
                                        className="px-2 py-0.5 text-xs rounded-md bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600"
                                      >
                                        {tag}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                
                                {/* Comment */}
                                {sessionReview.comment && (
                                  <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                                    "{sessionReview.comment}"
                                  </p>
                                )}
                              </div>
                            ) : (
                              /* No Review - Show full stars + "Rate this session" */
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingReview(null);
                                  setReviewingSessionId(chat.id);
                                  setReviewingSessionTitle(chat.title || 'Untitled Session');
                                  setIsReviewOpen(true);
                                }}
                                className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-yellow-600 dark:hover:text-yellow-500 transition-colors"
                              >
                                <div className="flex gap-0.5">
                                  {[1,2,3,4,5].map(i => (
                                    <svg key={i} className="w-4 h-4 text-gray-300 dark:text-gray-500 fill-current" viewBox="0 0 20 20">
                                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                                    </svg>
                                  ))}
                                </div>
                                <span>Rate this session</span>
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  No sessions found.
                </div>
              )}
            </div>
          </section>
        )}

        {activeTab === 'journal' && (
          <div className="space-y-8">
            
            {/* ═══════════════════════════════════════════════════════════
                PROJECT CONTEXT — For public viewers (read-only)
                Shows description + team to provide essential context
                ═══════════════════════════════════════════════════════════ */}
            {isReadOnlyViewer && (
              <section className="space-y-4">
                {/* Description */}
                {project.description && (
                  <div>
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
                      {project.description}
                    </p>
                  </div>
                )}
                
                {/* Team Members as Chips */}
                {allMembers.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mr-1">
                      Team
                    </span>
                    {allMembers.slice(0, 8).map((member) => {
                      const isProjectOwner = project?.ownerId === member.id;
                      return (
                        <button
                          key={member.id}
                          onClick={() => navigate(`/users/${member.username}`)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors group"
                        >
                          {member.avatar ? (
                            <img 
                              src={getImageUrl(member.avatar)} 
                              alt="" 
                              className="w-5 h-5 rounded-full object-cover"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ) : (
                            <div className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-[10px] font-bold">
                              {(member.username?.[0] || '?').toUpperCase()}
                            </div>
                          )}
                          <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white font-medium">
                            {member.username}
                          </span>
                          {isProjectOwner && (
                            <span className="text-[9px] bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 px-1 py-0.5 rounded font-bold">
                              HOST
                            </span>
                          )}
                        </button>
                      );
                    })}
                    {allMembers.length > 8 && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        +{allMembers.length - 8} more
                      </span>
                    )}
                  </div>
                )}
                
                {/* Separator */}
                {(project.description || allMembers.length > 0) && (
                  <div className="border-b border-gray-200 dark:border-gray-700" />
                )}
              </section>
            )}

            {/* ═══════════════════════════════════════════════════════════
                DISCOVERIES SECTION — Map + List as synchronized views
                Desktop: Split view | Mobile: Toggle with bottom sheet
                ═══════════════════════════════════════════════════════════ */}
            <DiscoveriesSection
              project={project}
              inventory={project?.world_state?.inventory || []}
              discoveryPins={discoveryPins}
              mapBounds={mapBounds}
              replayPath={shouldShowTrail ? replayPath : []}
              shouldShowTrail={shouldShowTrail}
              messagePins={messagePins}
              showMessagePins={showMessagePins}
            />

            {/* ACTIVITY TIMELINE — Chronological with left border */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white whitespace-nowrap mb-4">
                Activity Log
              </h3>
              {isLoadingEvents ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-600 dark:border-gray-400"></div>
                </div>
              ) : events.length === 0 ? (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  <p className="text-sm">No activity recorded yet.</p>
                </div>
              ) : (
                <div className="relative pl-6 border-l-2 border-gray-200 dark:border-gray-700 space-y-6">
                  {events.map((event) => {
                    const eventDate = new Date(event.createdAt);
                    const timeAgo = formatTimeAgo(eventDate);
                    
                    // Resolve actor username from members list or event content
                    const actorUsername =
                      (event.createdByUserId && memberById.get(String(event.createdByUserId))) ||
                      event.content?.found_by_username ||
                      null;

                    // Determine dot color and content based on event type
                    let dotColor = 'bg-gray-400';
                    let eventContent = null;
                    
                    if (event.type === 'NARRATIVE_UPDATE') {
                      // Never display — filtered server-side, but guard client-side too
                      return null;
                    } else if (event.type === 'INVENTORY_UPDATE') {
                      dotColor = 'bg-emerald-500';
                      const item = event.content;
                      eventContent = (
                        <div className="flex items-start gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              Found: {item.name || 'Unknown Item'}
                            </p>
                            {item.location_note && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{item.location_note}</p>
                            )}
                            {actorUsername && (
                              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                by @{actorUsername}
                              </p>
                            )}
                          </div>
                          {item.image_url && (
                            <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-200 dark:bg-gray-600 flex-shrink-0">
                              <img 
                                src={getImageUrl(item.image_url)} 
                                alt={item.name || "Item"} 
                                className="w-full h-full object-cover"
                                loading="lazy"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            </div>
                          )}
                        </div>
                      );
                    } else if (event.type === 'OBJECTIVE_UPDATE') {
                      dotColor = 'bg-purple-500';
                      const objective = event.content?.objective || '';
                      eventContent = (
                        <div>
                          <p className="text-sm text-gray-900 dark:text-white">
                            <span className="font-medium">New objective:</span> {objective}
                          </p>
                          {actorUsername && (
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">by @{actorUsername}</p>
                          )}
                        </div>
                      );
                    } else if (event.type === 'RELATIONSHIP_UPDATE') {
                      dotColor = 'bg-orange-500';
                      const rel = event.content;
                      eventContent = (
                        <div>
                          <p className="text-sm text-gray-700 dark:text-gray-300">
                            <span className="font-medium">{rel.source || 'Unknown'}</span>
                            {' → '}
                            <span className="text-gray-500">{rel.relation || 'related to'}</span>
                            {' → '}
                            <span className="font-medium">{rel.target || 'Unknown'}</span>
                          </p>
                          {actorUsername && (
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">by @{actorUsername}</p>
                          )}
                        </div>
                      );
                    } else {
                      // Generic fallback (OBJECTIVE_PROGRESS, OBJECTIVE_ARCHIVED, etc.)
                      eventContent = (
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {event.type.replace(/_/g, ' ').toLowerCase()}
                          </p>
                          {actorUsername && (
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">by @{actorUsername}</p>
                          )}
                        </div>
                      );
                    }
                    
                    if (!eventContent) return null;
                    
                    return (
                      <div key={event.id} className="relative">
                        {/* Timeline dot */}
                        <div className={`absolute -left-[31px] w-4 h-4 rounded-full ${dotColor} border-4 border-white dark:border-gray-900`} />
                        
                        {/* Event content */}
                        <div className="pb-2">
                          {eventContent}
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">
                            {timeAgo}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            {/* 4. DEBUG: World Model Snapshot — Members only, collapsed */}
            {hasFullProjectAccess && (
              <WorldModelDebugPanel
                worldState={project?.world_state}
                viewer={user}
                viewerContext={userContextQuery.data}
              />
            )}
            
          </div>
        )}

      </div>


      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[5000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 max-w-sm w-full shadow-2xl border border-gray-200 dark:border-gray-800">
            <div className="w-12 h-12 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center text-red-600 mb-4 mx-auto">
              <Trash2 className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-bold text-center mb-2 dark:text-white">Delete Project?</h3>
            <p className="text-center text-gray-500 text-sm mb-6">
              This action cannot be undone. All data, including chats and photos, will be permanently lost.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="py-3 rounded-xl font-semibold text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="py-3 rounded-xl font-semibold bg-red-600 text-white hover:bg-red-700 transition-colors shadow-lg shadow-red-600/20"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archive Confirmation Modal */}
      {showArchiveConfirm && (
        <div className="fixed inset-0 z-[5000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 max-w-sm w-full shadow-2xl border border-gray-200 dark:border-gray-800">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-400 mb-4 mx-auto">
              <Archive className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-bold text-center mb-2 dark:text-white">Archive Project?</h3>
            <p className="text-center text-gray-500 text-sm mb-6">
              This project will be marked as completed and moved to your project history. You can still access it later, but it will no longer appear in your active projects.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setShowArchiveConfirm(false)}
                className="py-3 rounded-xl font-semibold text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  archiveProjectMutation.mutate();
                  setShowArchiveConfirm(false);
                }}
                disabled={archiveProjectMutation.isPending}
                className="py-3 rounded-xl font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors shadow-lg shadow-blue-600/20 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {archiveProjectMutation.isPending ? 'Archiving...' : 'Archive'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Modal */}
      <ReviewModal 
        projectId={projectId}
        project={project}
        currentUser={user}
        isOpen={isReviewOpen}
        onClose={() => {
          setIsReviewOpen(false);
          setEditingReview(null);
          setReviewingSessionId(null);
          setReviewingSessionTitle(null);
        }}
        existingReview={editingReview}
        initialVisitedAt={initialVisitedAt}
        chatId={reviewingSessionId}
        sessionTitle={reviewingSessionTitle}
        sourceProjectId={projectId}
      />

      {/* Edit Project Modal */}
      <EditProjectModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        project={project}
        mode="edit"
        apiType="project"
        onSuccess={(updatedProject) => {
          // Invalidate project queries to refresh the data
          queryClient.invalidateQueries(createQueryKey.project(projectId));
          queryClient.invalidateQueries(['user-projects']);
          queryClient.invalidateQueries(['projects']);
        }}
      />

      <ProjectShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        onCopyLink={handleCopyShareLink}
        projectName={project?.name}
        projectUrl={shareUrl}
        previewImageUrl={sharePreviewImageUrl}
      />

      <ProjectReportModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        projectId={projectId}
        projectName={project?.name}
      />

      {/* Invite Member Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-[5000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 max-w-md w-full shadow-2xl border border-gray-200 dark:border-gray-800">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold dark:text-white">Invite Member</h3>
              <button onClick={() => setShowInviteModal(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-full">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Selected User Display */}
              {selectedUser && (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-500 dark:border-blue-400 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-indigo-500 text-white flex items-center justify-center font-bold text-sm">
                        {selectedUser.username[0].toUpperCase()}
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900 dark:text-white">@{selectedUser.username}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Selected</div>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedUser(null)}
                      className="p-1.5 hover:bg-blue-100 dark:hover:bg-blue-900/40 rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                    </button>
                  </div>
                </div>
              )}

              {/* Search Input - Only show when no user selected */}
              {!selectedUser && (
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Search User</label>
                  <input
                    type="text"
                    value={userSearchQuery}
                    onChange={(e) => { setUserSearchQuery(e.target.value); setSelectedUser(null); }}
                    placeholder="Type username..."
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none dark:text-white"
                  />
                  
                  {/* Only show searching when actually searching and no results yet */}
                  {isSearchingUsers && userSearchQuery.length >= 2 && searchUsers.length === 0 && (
                    <div className="mt-2 text-sm text-gray-500 text-center flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                      <span>Searching...</span>
                    </div>
                  )}
                  
                  {userSearchError && !isSearchingUsers && (
                    <div className="mt-2 text-sm text-red-500 text-center">Failed to search users</div>
                  )}
                  
                  {/* Search Results */}
                  {!isSearchingUsers && searchUsers.length > 0 && userSearchQuery.length >= 2 && (
                    <div className="mt-2 max-h-40 overflow-y-auto bg-white dark:bg-slate-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg">
                      {searchUsers.map(u => (
                        <button
                          key={u.id}
                          onClick={() => setSelectedUser(u)}
                          className="w-full text-left px-4 py-3 hover:bg-blue-50 dark:hover:bg-blue-900/20 flex items-center gap-3 transition-colors"
                        >
                          <div className="w-8 h-8 rounded-full bg-indigo-500 text-white flex items-center justify-center font-bold text-xs">
                            {u.username[0].toUpperCase()}
                          </div>
                          <span className="font-medium text-gray-900 dark:text-white">@{u.username}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {/* No results message */}
                  {!isSearchingUsers && userSearchQuery.length >= 2 && searchUsers.length === 0 && !userSearchError && (
                    <div className="mt-2 text-sm text-gray-500 text-center">No users found</div>
                  )}
                </div>
              )}

              <button
                onClick={() => {
                  if (selectedUser) addCollaboratorMutation.mutate({ userId: selectedUser.id });
                }}
                disabled={!selectedUser || addCollaboratorMutation.isPending}
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl shadow-lg shadow-blue-600/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {addCollaboratorMutation.isPending ? 'Adding...' : 'Add Member'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectView;

// Review modal mount helper
// eslint-disable-next-line no-unused-vars
const _ReviewModalMount = (props) => null;
